import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { catchError, filter, map, of, Subject, Subscription, switchMap, timer } from 'rxjs';
import {
  CandidateSearchSuggestion,
  CandidateSearchProfileViewContext,
  CandidateSearchSuggestionsResponse,
  getCandidateSearchDisplayName
} from '@app/models/candidate-search.model';
import {
  CandidateSearchHistoryEntry,
  CandidateSearchHistoryScope
} from '@app/models/candidate-search-history.model';
import { CandidateSearchHistoryService } from '@app/services/candidate-search-history.service';
import { TalentPoolService } from '@app/services/talentpool.service';
import { Label } from '@app/localization/providers/localized-text.provider';

interface SuggestionRequest {
  clientId: string;
  searchTerm: string;
}

interface SuggestionResult extends SuggestionRequest {
  response?: CandidateSearchSuggestionsResponse;
}

@Component({
  selector: 'app-global-candidate-search',
  templateUrl: './global-candidate-search.component.html',
  styleUrls: ['./global-candidate-search.component.scss']
})
export class GlobalCandidateSearchComponent implements OnInit, OnChanges, OnDestroy {
  private static nextInstanceId = 0;

  @Input() mobile = false;
  @Input() selectedClientId: string;
  @Input() memberUserId: string;
  @Input() rootClientId: string;

  @ViewChild('searchInput') searchInput: ElementRef<HTMLInputElement>;

  readonly localizedLabel = Label;
  readonly instanceId = GlobalCandidateSearchComponent.nextInstanceId++;

  searchTerm = '';
  isOpen = false;
  desktopExpanded = false;
  isLoading = false;
  hasMore = false;
  activeIndex = -1;
  suggestions: CandidateSearchSuggestion[] = [];
  recentEntries: CandidateSearchHistoryEntry[] = [];

  private readonly suggestionRequests = new Subject<SuggestionRequest>();
  private readonly subscriptions = new Subscription();
  private suggestionContext: CandidateSearchProfileViewContext;

  constructor(
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly router: Router,
    private readonly talentPoolService: TalentPoolService,
    private readonly historyService: CandidateSearchHistoryService
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(this.suggestionRequests.pipe(
      switchMap(request => {
        if (!request.clientId || request.searchTerm.length < 3) {
          return of<SuggestionResult>(request);
        }

        return timer(250).pipe(
          switchMap(() => this.talentPoolService
            .getCandidateSearchSuggestions(request.searchTerm, request.clientId)
            .pipe(
              map(response => ({ ...request, response })),
              catchError(() => of<SuggestionResult>({ ...request }))
            ))
        );
      })
    ).subscribe(result => this.applySuggestionResult(result)));

    this.subscriptions.add(this.historyService.changed$.subscribe(key => {
      if (key === this.historyService.getScopeKey(this.historyScope)) {
        this.loadRecentEntries();
      }
    }));

    this.subscriptions.add(this.router.events
      .pipe(filter(event => event instanceof NavigationStart))
      .subscribe(() => this.close(true)));

    this.loadRecentEntries();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.memberUserId || changes.rootClientId) {
      this.loadRecentEntries();
    }

    if (changes.selectedClientId && !changes.selectedClientId.firstChange && this.normalizedSearchTerm.length >= 3) {
      this.requestSuggestions();
    }
  }

  ngOnDestroy(): void {
    this.setMobileScrollLock(false);
    this.subscriptions.unsubscribe();
  }

  get normalizedSearchTerm(): string {
    return (this.searchTerm || '').trim().replace(/\s+/g, ' ');
  }

  get isShowingRecentEntries(): boolean {
    return !this.normalizedSearchTerm;
  }

  get activeDescendantId(): string | null {
    return this.activeIndex >= 0 ? `global-search-${this.instanceId}-option-${this.activeIndex}` : null;
  }

  open(): void {
    this.isOpen = true;
    this.activeIndex = -1;

    if (this.isShowingRecentEntries) {
      this.loadRecentEntries();
    } else if (this.normalizedSearchTerm.length >= 3 && !this.suggestions.length) {
      this.requestSuggestions();
    }
  }

  openMobile(): void {
    this.open();
    this.setMobileScrollLock(true);
    setTimeout(() => this.searchInput?.nativeElement.focus());
  }

  openDesktop(): void {
    this.desktopExpanded = true;
    this.open();
    setTimeout(() => this.searchInput?.nativeElement.focus());
  }

  close(clearSearch = false): void {
    this.isOpen = false;
    this.desktopExpanded = false;
    this.activeIndex = -1;
    this.setMobileScrollLock(false);

    if (clearSearch) {
      this.suggestionContext = null;
      this.searchTerm = '';
      this.suggestions = [];
      this.hasMore = false;
      this.isLoading = false;
      this.suggestionRequests.next({ clientId: this.selectedClientId, searchTerm: '' });
    }
  }

  onSearchTermChanged(): void {
    this.suggestionContext = null;
    this.isOpen = true;
    this.activeIndex = -1;
    this.suggestions = [];
    this.hasMore = false;
    this.isLoading = this.normalizedSearchTerm.length >= 3 && !!this.selectedClientId;
    this.suggestionRequests.next({
      clientId: this.selectedClientId,
      searchTerm: this.normalizedSearchTerm
    });
  }

  onKeydown(event: KeyboardEvent): void {
    const optionCount = this.isShowingRecentEntries ? this.recentEntries.length : this.suggestions.length;

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (!optionCount) {
        return;
      }

      event.preventDefault();
      const direction = event.key === 'ArrowDown' ? 1 : -1;
      this.activeIndex = this.activeIndex < 0
        ? (direction > 0 ? 0 : optionCount - 1)
        : (this.activeIndex + direction + optionCount) % optionCount;
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      this.activateCurrentSelection();
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      this.close(this.mobile);
      this.searchInput?.nativeElement.blur();
      return;
    }

    if (event.key === 'Tab') {
      this.isOpen = false;
      this.activeIndex = -1;
      this.setMobileScrollLock(false);
      setTimeout(() => this.desktopExpanded = false);
    }
  }

  activateRecent(entry: CandidateSearchHistoryEntry): void {
    if (entry.type === 'search') {
      this.commitSearch(entry.searchTerm);
      return;
    }

    this.openProfile(entry.candidateId);
  }

  activateSuggestion(suggestion: CandidateSearchSuggestion): void {
    this.talentPoolService.recordSearchProfileView(suggestion.candidateId, this.suggestionContext);
    this.openProfile(suggestion.candidateId);
  }

  removeRecent(event: Event, entry: CandidateSearchHistoryEntry): void {
    event.preventDefault();
    event.stopPropagation();
    this.historyService.remove(this.historyScope, entry.id);
    this.activeIndex = -1;
  }

  seeAllResults(): void {
    this.commitSearch(this.normalizedSearchTerm);
  }

  suggestionDisplayName(suggestion: CandidateSearchSuggestion): string {
    return getCandidateSearchDisplayName(suggestion);
  }

  trackByEntryId(_index: number, entry: CandidateSearchHistoryEntry): string {
    return entry.id;
  }

  trackByCandidateId(_index: number, suggestion: CandidateSearchSuggestion): string {
    return suggestion.candidateId;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.mobile && this.isOpen && !this.elementRef.nativeElement.contains(event.target as Node)) {
      this.close();
    }
  }

  private get historyScope(): CandidateSearchHistoryScope {
    return {
      memberUserId: this.memberUserId,
      rootClientId: this.rootClientId
    };
  }

  private requestSuggestions(): void {
    this.suggestionContext = null;
    this.isLoading = !!this.selectedClientId;
    this.suggestions = [];
    this.hasMore = false;
    this.suggestionRequests.next({
      clientId: this.selectedClientId,
      searchTerm: this.normalizedSearchTerm
    });
  }

  private applySuggestionResult(result: SuggestionResult): void {
    if (result.clientId !== this.selectedClientId || result.searchTerm !== this.normalizedSearchTerm) {
      return;
    }

    this.isLoading = false;
    this.suggestions = result.response?.items || [];
    this.suggestionContext = result.response ? {
      clientId: result.clientId,
      searchTerm: result.searchTerm,
      resultCount: result.response.totalCount
    } : null;
    this.hasMore = result.response?.hasMore || false;
    this.activeIndex = -1;
  }

  private setMobileScrollLock(locked: boolean): void {
    if (!this.mobile) {
      return;
    }

    document.body.classList.toggle('global-candidate-search-open', locked);
  }

  private activateCurrentSelection(): void {
    if (this.activeIndex >= 0) {
      if (this.isShowingRecentEntries) {
        this.activateRecent(this.recentEntries[this.activeIndex]);
      } else if (this.suggestions[this.activeIndex]) {
        this.activateSuggestion(this.suggestions[this.activeIndex]);
      }
      return;
    }

    this.commitSearch(this.normalizedSearchTerm);
  }

  private commitSearch(searchTerm: string): void {
    const normalizedTerm = (searchTerm || '').trim().replace(/\s+/g, ' ');
    const hasSearchableToken = normalizedTerm.split(/\s+/).some(token => token.length >= 2);
    if (!hasSearchableToken) {
      return;
    }

    this.historyService.recordSearch(this.historyScope, normalizedTerm);
    this.close(true);
    this.router.navigate(['/talent/list'], {
      queryParams: {
        talentType: 'approvedTalent',
        searchTerm: normalizedTerm
      }
    });
  }

  private openProfile(candidateId: string): void {
    if (!candidateId) {
      return;
    }

    this.close(true);
    this.router.navigate(['/talent/profile'], {
      queryParams: {
        id: candidateId,
        type: 'approvedTalent'
      }
    });
  }

  private loadRecentEntries(): void {
    this.recentEntries = this.historyService.getEntries(this.historyScope, 5);
  }
}
